import React, {
  useState, useRef, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { graphql } from '@apollo/client/react/hoc';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import history from 'utils/history';
import PATIENT_RECORD from 'graph/queries/patientRecord';
import CURRENT_USER from 'graph/queries/currentUser';
import Dropdown, { Menu, MenuItem, Divider } from 'common/Dropdown';
import { logoutUser } from 'redux/auth/actions';

const mapDispatchToProps = dispatch => (
  {
    onLogout: () => { dispatch(logoutUser()); },
  }
);

const PROFILE_MENU_ID = 'header-profile-menu';

function HeaderProfile({ patientRecord: { given, organization }, currentUser: { username }, onLogout }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const triggerRef = useRef(null);
  const openWithKeyboardRef = useRef(false);

  const scheduleFocusFirstMenuItem = useCallback((menuId) => {
    const tryFocus = () => {
      const root = document.getElementById(menuId);
      if (!root) {
        requestAnimationFrame(tryFocus);
        return;
      }
      const first = root.querySelector('[role="menuitem"]:not([aria-disabled="true"])');
      if (first && typeof first.focus === 'function') {
        first.focus();
      }
    };
    requestAnimationFrame(tryFocus);
  }, []);

  const handleVisibleChange = useCallback((visible) => {
    setMenuOpen(visible);
    if (!visible) {
      openWithKeyboardRef.current = false;
      return;
    }
    if (!openWithKeyboardRef.current) {
      return;
    }
    openWithKeyboardRef.current = false;
    scheduleFocusFirstMenuItem(PROFILE_MENU_ID);
  }, [scheduleFocusFirstMenuItem]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setMenuOpen(false);
        if (triggerRef.current && typeof triggerRef.current.focus === 'function') {
          triggerRef.current.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [menuOpen]);

  function handleClick(info) {
    const { key } = info;
    switch (key) {
      case 'logout':
        onLogout();
        break;
      default:
        history.push('/settings');
    }
  }

  const menu = (
    <Menu id={PROFILE_MENU_ID} onClick={handleClick}>
      <MenuItem key="username" disabled className="inactiveMenuItem">
        <i className="fas fa-user mr2" />
        {username}
      </MenuItem>
      <Divider />
      <MenuItem key="settings">
        <i className="fas fa-cog mr2" />
        Settings
      </MenuItem>
      <MenuItem key="logout">
        <i className="fas fa-power-off mr2" />
        Log Out
      </MenuItem>
    </Menu>
  );
  return (
    <Dropdown
      trigger={['click']}
      overlay={menu}
      animation="slide-up"
      visible={menuOpen}
      onVisibleChange={handleVisibleChange}
    >
      <button
        type="button"
        id="header-profile"
        ref={triggerRef}
        className="flex items-center mr2 fw4"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-controls={PROFILE_MENU_ID}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            openWithKeyboardRef.current = true;
            return;
          }
          if (!menuOpen && e.key === 'ArrowDown') {
            e.preventDefault();
            setMenuOpen(true);
            scheduleFocusFirstMenuItem(PROFILE_MENU_ID);
          }
        }}
      >
        <div id="header-login-text" className="pr3 pl3 pointer silver">
          <div className="header-login-text">
            <span className="primary ttc f6">{given}</span>
            <br />
            <span className="primary ttc f6">{organization.name}</span>
            <i className="fas fa-angle-down ml2 fw-1" aria-hidden />
          </div>
        </div>
      </button>
    </Dropdown>
  );
}

HeaderProfile.propTypes = {
  patientRecord: PropTypes.shape({
    id: PropTypes.string,
    given: PropTypes.string,
    organization: PropTypes.shape({
      name: PropTypes.string,
    })
  }),
  currentUser: PropTypes.shape({
    id: PropTypes.string,
    username: PropTypes.string,
  }),
  onLogout: PropTypes.func.isRequired,
};

HeaderProfile.defaultProps = {
  patientRecord: {
    id: '',
    given: '',
    organization: {
      name: '',
    },
  },
  currentUser: {
    id: '',
    username: '',
  },
}

export default compose(
  connect(null, mapDispatchToProps),
  withRouter,
  graphql(PATIENT_RECORD, {
    options: { fetchPolicy: 'cache-and-network' },
    props: ({ data: { patientRecord } }) => ({
      patientRecord,
    }),
  }),
  graphql(CURRENT_USER, {
    options: { fetchPolicy: 'cache-and-network' },
    props: ({ data: { currentUser } }) => ({
      currentUser,
    }),
  }),
)(HeaderProfile);
